/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { AsyncResult, CartLine, QueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import {
    getPaginatedOrderTemplateLinesAction,
    GetPaginatedOrderTemplateLinesInput,
    mapAggregatedProductInventoryInformation,
    IProductInventoryInformation,
    createInventoryAvailabilitySearchCriteria
} from '@msdyn365-commerce-modules/retail-actions';
import { addCartLinesAsync, updateAsync, updateCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { checkLineInventory } from './check-line-inventory';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { addAttribute, _makeAttribute } from '../utilities/helpers';
import { IOutOfStockOrderTemplateLinesData, OutOfStockOrderTemplateLinesInput } from './out-of-stock-order-template-lines.action';

/**
 * CustomAddOrderTemplateToCart Input Action
 */
export class CustomAddOrderTemplateToCartInput implements IActionInput {
    public readonly orderTemplateId: string;

    constructor(orderTemplateId: string) {
        this.orderTemplateId = orderTemplateId;
    }
    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `TODO`;
    public getCacheObjectType = () => 'TODO';
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the AddOrderTemplateToCart method.
 * @param {ICreateActionContext} inputData The input data passed to the createInput method.
 * @param {string} orderTemplateId The id of template.
 */
export const customAddOrderTemplateToCartInput = (
    inputData: ICreateActionContext,
    orderTemplateId: string
): CustomAddOrderTemplateToCartInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to create order template. User is not authenticated.');
    }
    return new CustomAddOrderTemplateToCartInput(orderTemplateId);
};

/**
 * The action method for the AddOrderTemplateToCart data action.
 * @param {CustomAddOrderTemplateToCartInput} input The action input.
 * @param {IActionContext} ctx The action context.
 * @param context
 */
export async function customAddOrderTemplateToCartAction(
    input: CustomAddOrderTemplateToCartInput,
    context: IActionContext
): Promise<CartLine[]> {
    const cartState = await getCartState(context);
    const chunkCount = 1000;
    const queryResultSettings: QueryResultSettings = {
        Paging: {
            Top: chunkCount,
            Skip: 0
        }
    };
    const getPaginatedOrderTemplateLinesInput = new GetPaginatedOrderTemplateLinesInput(input.orderTemplateId, queryResultSettings);
    let orderTemplateLines = await getPaginatedOrderTemplateLinesAction(getPaginatedOrderTemplateLinesInput, context);
    let addedCartLines: CartLine[] = [];
    const cartLinesToUpdate: CartLine[] = [];
    let productAvailabilities: IProductInventoryInformation[] = [];
    for (let skip = chunkCount; orderTemplateLines.lines.length > 0; skip += chunkCount) {
        const cartLines: CartLine[] = orderTemplateLines.lines.map(productLine => {
            const cartLine: CartLine = {
                ItemId: productLine.fullProduct?.ProductDetails.ItemId,
                ProductId: productLine.productListLine.ProductId,
                Quantity: productLine.productListLine.Quantity,
                TrackingId: '',
                UnitOfMeasureSymbol: productLine.productListLine.UnitOfMeasure
            };
            return cartLine;
        });
        // VSI Customization
        const productIds = cartLines.map(line => line.ProductId!);
        const searchCriteria = createInventoryAvailabilitySearchCriteria(context, productIds, true);
        const linesProductAvailabilities = await getEstimatedAvailabilityAsync(
            { callerContext: context, bypassCache: 'get' },
            searchCriteria
        );
        if (
            linesProductAvailabilities &&
            linesProductAvailabilities.ProductWarehouseInventoryAvailabilities &&
            linesProductAvailabilities.ProductWarehouseInventoryAvailabilities.length > 0
        ) {
            productAvailabilities = mapAggregatedProductInventoryInformation(context, linesProductAvailabilities);
        }
        for (const [i, cartline] of cartLines.entries()) {
            const lineResult = await checkLineInventory(
                context,
                cartline.ProductId!,
                cartline.Quantity!,
                productAvailabilities,
                cartState.cart
            );
            if (!lineResult.isValidQuantity) {
                const templateLine = orderTemplateLines.lines.find(
                    productLine => productLine.productListLine.ProductId === cartline.ProductId!
                );
                const outOfStockItems = context.get('OutOfStockOrderTemplateLinesObject', 'OutOfStockOrderTemplateLines') as AsyncResult<
                    IOutOfStockOrderTemplateLinesData
                >;
                const outOfStockItemsData = outOfStockItems && outOfStockItems.result?.outOfStockItems;
                if (templateLine && outOfStockItemsData) {
                    let outOfStockItems = [...outOfStockItemsData];
                    outOfStockItems.push(templateLine.productListLine.LineId!);
                    outOfStockItems = [...new Set(outOfStockItems)];
                    context.update(new OutOfStockOrderTemplateLinesInput(), {
                        outOfStockItems
                    });
                }
                cartLines.splice(i, 1);
            } else {
                cartline.Quantity = lineResult.lineQuantity;
                cartline.ExtensionProperties = [];
                cartline.ExtensionProperties?.push({
                    Key: 'threshold',
                    Value: {
                        StringValue: lineResult.thresholdQuantity.toString()
                    }
                });
            }
        }
        if (cartLines.length === 0) {
            throw new Error('Unable to add order template to cart.');
        }
        // VSI Customization End
        const updatedCart = await addCartLinesAsync({ callerContext: context }, cartState.cart.Id, cartLines, cartState.cart.Version).catch(
            error => {
                context.telemetry.trace(error);
                context.telemetry.trace('Unable to add order template to cart.');
                throw error;
            }
        );
        addedCartLines = addedCartLines.concat(cartLines);
        // VSI Customization
        updatedCart.CartLines?.forEach(cartLine => {
            const existingLine = addedCartLines.find(line => line.ProductId === cartLine.ProductId);
            if (existingLine && existingLine.ExtensionProperties) {
                cartLine.ExtensionProperties = [...existingLine.ExtensionProperties];
                cartLinesToUpdate.push(cartLine);
            }
        });
        // VSI Customization End
        getPaginatedOrderTemplateLinesInput.queryResultSettings.Paging!.Skip = skip;
        orderTemplateLines = await getPaginatedOrderTemplateLinesAction(getPaginatedOrderTemplateLinesInput, context);
    }
    // VSI Customization
    for (const line of cartLinesToUpdate) {
        const threshold = line.ExtensionProperties?.find(property => property.Key === 'threshold');
        if (line.AttributeValues) {
            let cartLineAttributes = [...line.AttributeValues];
            const quantityThresholdAttributeName = context.requestContext.app.config.quantityThresholdAttributeName;
            const attributeObj = _makeAttribute(quantityThresholdAttributeName, threshold?.Value?.StringValue);
            cartLineAttributes = addAttribute(cartLineAttributes, attributeObj);
            line.AttributeValues = cartLineAttributes;
        }
    }
    if (cartLinesToUpdate.length === 0) {
        throw new Error('Unable to add order template to cart.');
    }
    const updatedCart = await updateCartLinesAsync({ callerContext: context }, cartState?.cart.Id || '', cartLinesToUpdate);
    await updateAsync({ callerContext: context }, updatedCart);
    // VSI Customization End
    await cartState.refreshCart({});
    return addedCartLines;
}

export const customAddOrderTemplateToCartActionDataAction = createObservableDataAction<CartLine[]>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/add-order-template-to-cart',
    action: <IAction<CartLine[]>>customAddOrderTemplateToCartAction,
    input: customAddOrderTemplateToCartInput
});
export default customAddOrderTemplateToCartActionDataAction;
